import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { JsonMap, WorkOrderOverview } from '../work-order'
import { ElderQuantityPipe, ElderTableComponent, Quantity, Unit } from '@elderbyte/ngx-starter'
import { filter, map } from 'rxjs/operators'
import { ProcessPlanService } from '../../../process/process-plans/process-plan.service'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { AsyncPipe } from '@angular/common'
import { truthyPredicate } from '../../../../../functional.util'

class OverviewRow {
  constructor(
    public readonly name: string,
    public readonly itemCount: number,
    public readonly weight: Quantity,
    public readonly pieces: Quantity = Quantity.zero(Unit.pc),
    public readonly quantities: JsonMap<Quantity>
  ) {}
}

@Component({
  selector: 'app-work-order-overview',
  templateUrl: './work-order-overview.component.html',
  styleUrls: ['./work-order-overview.component.scss'],
  imports: [
    ElderTableComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
  ],
})
export class WorkOrderOverviewComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly overview$ = new BehaviorSubject<WorkOrderOverview | null>(null)
  public readonly rows$: Observable<OverviewRow[]>

  public readonly Unit = Unit

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const processPlanService = inject(ProcessPlanService)
    this.rows$ = combineLatest([this.overview$, processPlanService.processPlans$]).pipe(
      filter(([overview, _]) => truthyPredicate(overview)),
      map(([overview, processPlans]) => {
        const rows: OverviewRow[] = [
          new OverviewRow(
            'Total',
            overview.aggregate.total.numberOfItems,
            overview.aggregate.total.weight,
            overview.aggregate.total.quantities[Unit.pc],
            overview.aggregate.total.quantities
          ),
        ]

        Object.keys(overview.aggregate.processPlan).forEach((key) => {
          const ppag = overview.aggregate.processPlan[key]
          rows.push(
            new OverviewRow(
              processPlans.find((pp) => pp.id === key)?.name || key,
              ppag.data.numberOfItems,
              ppag.data.weight,
              ppag.data.quantities[Unit.pc],
              ppag.data.quantities
            )
          )
        })

        return rows
      })
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set overview(overview: WorkOrderOverview) {
    this.overview$.next(overview)
  }
}
